import React, { useEffect, useState } from 'react';
import GatsbyImage from 'gatsby-image';
import { Location } from '@reach/router';
import messages from './NavBar.messages';
import { useIntl } from 'react-intl';
import { useStaticQuery, graphql } from 'gatsby';
import lodashDebounce from 'lodash/debounce';
import styles from './NavBar.module.scss';
import IwLink from '../IwLink/IwLink';
import IwocaLogoSVG from '../../SVGComponents/IwocaLogoSVG';
import { PrimaryCta as Button } from '../../templates/MCA/components/PrimaryCta/PrimaryCta';
import TelIconSVG from '../../SVGComponents/TelIconSVG';
import DropdownMenu from './DropdownMenu/DropdownMenu';
import InlineMenu from './InlineMenu/InlineMenu';
import HamburgerMenu from './DropdownMenu/HamburgerMenu/HamburgerMenu';
import LockIcon from '../../SVGComponents/LockIcon';
import SubNav from './SubNav/SubNav';
import classnames from 'classnames';
import { fetchIsLoggedIn } from '../../util/authHelpers';
import { getFullIwocaUrl } from 'aphrodite-shared/util/helpers';
import { DEFAULT_PHONE_NUMBER } from 'aphrodite-shared/util/iwocaConstants';
import IwContext from 'aphrodite-shared/IwContext/IwContext';
import { useContext } from 'react';
import ErrorBar from 'aphrodite-shared/components/ErrorBar/ErrorBar';
import { Amplitude, AmplitudeWebsite } from '@iwoca/frontend-tracking-library';
import { useProductName } from '../../components/IwModals/StepZeroModal/StepZeroModalHelpers';
import IwModals from '../IwModals/IwModals';

let hasScrolled = false;

const useIsScroll = () => {
    const [navBar, setNavBar] = useState(true);

    if (typeof window !== 'undefined') {
        let prevScrollpos = window.pageYOffset;
        window.onscroll = lodashDebounce(() => {
            let currentScrollPos = window.pageYOffset;
            if (prevScrollpos > currentScrollPos || currentScrollPos < 50) {
                setNavBar(true);
            } else {
                setNavBar(false);
            }
            prevScrollpos = currentScrollPos;
        }, 1);
    }

    return navBar;
};

function useNavbarData(isIwocaPay) {
    const {
        allContentfulNavbar: { nodes: navBars }
    } = useStaticQuery(
        graphql`
            {
                allContentfulNavbar {
                    nodes {
                        name
                        menuItems {
                            name
                            url
                            item {
                                url
                                title
                                dataGaId
                            }
                        }
                    }
                }
            }
        `
    );
    const navBarName = isIwocaPay ? 'iwocaPay Navbar' : 'Core Navbar';

    return navBars.find((navBar) => navBar.name === navBarName) || navBars[0];
}

function getIsProductPage(pathname) {
    switch (pathname) {
        case '/recovery-loan-scheme/':
        case '/lightning-loan/':
        case '/flexi-loan/':
        case '/cash-advance/':
        case '/iwoca-cash-advance/':
            return true;
        default:
            return false;
    }
}

const trackNavBarCta = (ctaText, productName) => {
    const navBarCtaEvent = AmplitudeWebsite.getStep0ModalEventWithProductTypeAndText(
        AmplitudeWebsite.OPEN_NAV_STEP_0_MODAL,
        ctaText,
        productName
    );
    Amplitude.track(navBarCtaEvent);
};

const NavBar = ({
    isIwocaPay = false,
    className,
    HomeLogo,
    containerClassName,
    isLandingPage = false,
    partnershipLogo,
    ctaButton,
    displayCtaButton = true,
    subNavData,
    hidePhoneNumber = false,
    hideLoginLink = false,
    phoneNumber,
    phoneNumberLink,
    primaryCtaLink,
    loginButton,
    children,
    maximumLoanAmount
}) => {
    const loggedIn = fetchIsLoggedIn();

    const productName = useProductName();
    const [isOpen, setOpen] = useState(false);
    const [isProductPage, setIsProductPage] = useState(false);

    useEffect(() => {
        setIsProductPage(getIsProductPage(window.location.pathname));
        Amplitude.initialize();
        Amplitude.identify();
        Amplitude.track(AmplitudeWebsite.getPageVisitedEvent());
    }, []);

    const { formatMessage } = useIntl();

    const onClick = () => setOpen(!isOpen);

    const globalContext = useContext(IwContext);

    const gaID = ctaButton ? ctaButton.dataGaId : 'navbar_cta__cta';
    const primaryBtn = loggedIn
        ? {
              txt: 'My account',
              href: isIwocaPay ? '/pay/dashboard/' : '/my_account/',
              gaID: 'navbar__account',
              id: 'my_account'
          }
        : {
              txt: isIwocaPay ? 'Check eligibility' : 'Apply now',
              href: isIwocaPay
                  ? '/pay/onboarding/eligibility/'
                  : isProductPage
                  ? '#apply'
                  : '#compare',
              gaID,
              id: isIwocaPay ? 'check_eligibility' : 'apply'
          };

    function handleOpenFlexiStepZero() {
        if (loggedIn) {
            window.location.href = isIwocaPay
                ? 'pay_dashboard'
                : '/my_account/';
        } else {
            trackNavBarCta(messages[primaryBtn.id], productName);
            window.location.hash = isProductPage ? '#apply' : '#compare';
        }
    }

    function handleOpenIwocaPay() {
        window.location.href = loggedIn
            ? '/pay/dashboard/'
            : '/pay/onboarding/eligibility/';
    }

    const isScroll = useIsScroll();

    const navBarActivity = isScroll ? '' : styles.InactiveNavBar;
    const primaryCtaButtonProps = {
        colour: 'primary',
        buttonClass: classnames(
            styles.ApplyButton,
            isLandingPage && styles.landingApplyButton,
            hideLoginLink && hidePhoneNumber && styles.standaloneCta,
            isIwocaPay && styles.iwocaPayNavButton
        ),
        dataGaId: primaryBtn.gaID
    };

    const data = useNavbarData(isIwocaPay);

    // Handling phone number translation in advance and ensureing phoneNumber object is always available
    return (
        <>
            <div style={{ position: 'relative' }}>
                <IwModals maximumLoanAmount={maximumLoanAmount} />
                <div
                    className={`${styles.NavBarContainer} ${navBarActivity} ${containerClassName}`}>
                    <nav
                        className={`${styles.NavBar} ${className} ${
                            isLandingPage && !hasScrolled
                                ? styles.landingPageNav
                                : ''
                        }`}>
                        <div className={styles.paneContainer}>
                            <section className={styles.LeftPane}>
                                {HomeLogo ? (
                                    <HomeLogo />
                                ) : (
                                    <IwLink to="/" className={styles.logoLink}>
                                        <IwocaLogoSVG className={styles.logo} />
                                    </IwLink>
                                )}
                                {partnershipLogo && (
                                    <GatsbyImage
                                        className={styles.PartnershipLogo}
                                        fluid={partnershipLogo.fluid}
                                        imgStyle={{ objectFit: 'scale-down' }}
                                    />
                                )}
                                {!isLandingPage && (
                                    <InlineMenu menuSections={data.menuItems} />
                                )}
                            </section>
                            <section className={styles.Actions}>
                                <span
                                    className={classnames(
                                        styles.ActionsWrapper,
                                        {
                                            [styles.landingActionsWrapper]: isLandingPage
                                        }
                                    )}>
                                    {!loggedIn && !hideLoginLink && (
                                        <IwLink
                                            to={`https://${getFullIwocaUrl(
                                                (loginButton &&
                                                    loginButton.href) ||
                                                    '/login/'
                                            )}`}
                                            className={styles.Login}
                                            data-ga-id="navbar__link">
                                            <LockIcon
                                                className={styles.LockIcon}
                                            />
                                            {formatMessage(messages.login)}
                                        </IwLink>
                                    )}
                                    {(!isLandingPage ||
                                        (isLandingPage && hasScrolled)) &&
                                        displayCtaButton &&
                                        (ctaButton ? (
                                            <Button
                                                href={ctaButton.href}
                                                {...primaryCtaButtonProps}>
                                                {ctaButton.text ||
                                                    formatMessage(
                                                        messages[primaryBtn.id]
                                                    )}
                                            </Button>
                                        ) : (
                                            <Button
                                                onClick={() => {
                                                    isIwocaPay
                                                        ? handleOpenIwocaPay()
                                                        : handleOpenFlexiStepZero();
                                                }}
                                                {...primaryCtaButtonProps}>
                                                {/* in here */}
                                                {formatMessage(
                                                    messages[primaryBtn.id]
                                                )}
                                            </Button>
                                        ))}
                                    <HamburgerMenu
                                        isOpen={isOpen}
                                        onClick={onClick}
                                    />

                                    <section className={styles.Telephone}>
                                        <TelIconSVG />
                                        <a
                                            href={
                                                phoneNumberLink
                                                    ? phoneNumberLink
                                                    : DEFAULT_PHONE_NUMBER.en
                                                          .link
                                            }
                                            data-ga-id="navbar__phone">
                                            {phoneNumber
                                                ? phoneNumber
                                                : DEFAULT_PHONE_NUMBER.en
                                                      .display}
                                        </a>
                                    </section>
                                </span>
                            </section>
                        </div>
                        {subNavData && subNavData.length > 0 && (
                            <SubNav subNavData={subNavData} />
                        )}
                    </nav>
                    <DropdownMenu
                        isOpen={isOpen}
                        menuSections={data.menuItems}
                        isLandingPage={isLandingPage}
                        isLoggedIn={loggedIn}
                        phoneNumber={phoneNumber}
                        primaryCta={{
                            href: primaryCtaLink
                        }}
                    />
                    {children}
                    {globalContext.apiErrorMessage && (
                        <ErrorBar message={globalContext.apiErrorMessage} />
                    )}
                </div>
            </div>
        </>
    );
};

export default NavBar;
